import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { formatCLP, dateLabel, statusBadge } from "../lib/format";
import { StarRating } from "../components/StarRating";
import { MapPin, MapPinLine, Package, CalendarBlank, ArrowLeft, CircleNotch, Users, XCircle, CheckCircle, Star, Truck } from "@phosphor-icons/react";

export default function FreightDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [freight, setFreight] = useState(null);
  const [offers, setOffers] = useState([]);
  const [selecting, setSelecting] = useState(null);

  const load = async () => {
    try {
      const f = await api.get(`/freights/${id}`);
      setFreight(f.data);
      if (f.data.status !== "CANCELADO") {
        const o = await api.get(`/freights/${id}/offers`);
        setOffers(o.data);
      }
    } catch { toast.error("No se pudo cargar el flete"); }
  };
  useEffect(() => { load(); }, [id]);

  const select = async (offerId) => {
    setSelecting(offerId);
    try {
      const { data } = await api.post(`/freights/${id}/select`, { offerId });
      toast.success("¡Transportista seleccionado!");
      navigate(`/app/service/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
      setSelecting(null);
    }
  };

  const cancel = async () => {
    try {
      await api.post(`/freights/${id}/cancel`);
      toast.success("Flete cancelado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  if (!freight) return <div className="flex justify-center py-24"><CircleNotch size={36} className="animate-spin text-[#FF5A36]" /></div>;

  const badge = statusBadge(freight.status);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="freight-detail">
      <button onClick={() => navigate("/app/client")} className="flex items-center gap-1 text-sm font-semibold text-zinc-600 hover:text-[#0F1115] mb-6">
        <ArrowLeft size={16} weight="bold" /> Mis fletes
      </button>

      <div className="rounded-2xl border border-zinc-200 bg-white p-6 mb-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-[#FF5A36]">{freight.category}</span>
            <h1 className="font-display text-2xl font-bold tracking-tight mt-1">{freight.origin.city} → {freight.destination.city}</h1>
          </div>
          <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${badge.color}`}>{badge.label}</span>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mt-5">
          <Info icon={MapPin} label="Origen" value={`${freight.origin.address}, ${freight.origin.city}`} />
          <Info icon={MapPinLine} label="Destino" value={`${freight.destination.address}, ${freight.destination.city}`} />
          <Info icon={Package} label="Carga" value={freight.description || freight.category} />
          <Info icon={CalendarBlank} label="Cuándo" value={freight.asap ? "Lo antes posible" : `${dateLabel(freight.requestedDate)} · ${freight.timeRange}`} />
        </div>
        <div className="border-t border-zinc-100 mt-5 pt-5 flex items-center justify-between">
          <span className="text-sm text-zinc-500">Precio ofrecido</span>
          <span className="font-display text-3xl font-black text-[#0F1115]">{formatCLP(freight.proposedPrice)}</span>
        </div>
        {freight.status === "BUSCANDO_TRANSPORTISTA" && (
          <button onClick={cancel} data-testid="cancel-freight-btn"
            className="mt-4 w-full py-3 rounded-xl border border-red-200 text-red-600 font-semibold flex items-center justify-center gap-2 hover:bg-red-50 transition-colors">
            <XCircle size={18} weight="bold" /> Cancelar flete
          </button>
        )}
      </div>

      {freight.status === "CANCELADO" ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center text-red-700 font-semibold">Este flete fue cancelado</div>
      ) : (
        <section>
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Users size={20} /> Transportistas interesados
            <span className="text-sm font-normal text-zinc-500">({offers.length})</span>
          </h2>
          {offers.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-zinc-300 bg-white p-10 text-center text-zinc-500" data-testid="no-offers">
              Aún no hay propuestas. Los transportistas acreditados verán tu flete pronto.
            </div>
          ) : (
            <>
              {offers.length > 1 && freight.status !== "ADJUDICADO" && (
                <div className="mb-5 overflow-x-auto rounded-2xl border border-zinc-200 bg-white" data-testid="offers-comparison">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-widest text-zinc-400 border-b border-zinc-100">
                        <th className="px-4 py-3 font-bold">Transportista</th>
                        <th className="px-4 py-3 font-bold">Precio</th>
                        <th className="px-4 py-3 font-bold">Rating</th>
                        <th className="px-4 py-3 font-bold">Viajes</th>
                        <th className="px-4 py-3 font-bold">Vehículo</th>
                        <th className="px-4 py-3 font-bold"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...offers].sort((a, b) => a.offeredPrice - b.offeredPrice).map((o) => (
                        <tr key={o.id} className="border-b border-zinc-50 last:border-0 hover:bg-zinc-50" data-testid={`compare-row-${o.id}`}>
                          <td className="px-4 py-3">
                            <span className="font-semibold flex items-center gap-1">{o.carrier.name}
                              {o.driver?.verified && <CheckCircle size={13} weight="fill" className="text-emerald-500" title="Conductor verificado" />}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-display font-black">{formatCLP(o.offeredPrice)}</td>
                          <td className="px-4 py-3"><span className="flex items-center gap-1"><Star size={13} weight="fill" className="text-[#FF5A36]" />{o.carrier.rating || "—"}</span></td>
                          <td className="px-4 py-3">{o.carrier.completedServices}</td>
                          <td className="px-4 py-3 text-zinc-600">{o.vehicle ? `${o.vehicle.type}${o.vehicle.licensePlate ? ` · ${o.vehicle.licensePlate}` : ""}` : "—"}</td>
                          <td className="px-4 py-3 text-right">
                            <button onClick={() => select(o.id)} disabled={selecting} data-testid={`compare-select-${o.id}`}
                              className="px-3 py-1.5 rounded-full bg-[#FF5A36] text-white text-xs font-bold hover:bg-[#E04B2A] disabled:opacity-60 active:scale-95">Elegir</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="space-y-3" data-testid="offers-list">
              {offers.map((o) => {
                const isSelected = freight.acceptedOfferId === o.id;
                const disabled = freight.status === "ADJUDICADO";
                return (
                  <div key={o.id} data-testid={`offer-${o.id}`}
                    className={`rounded-2xl border p-5 bg-white ${isSelected ? "border-emerald-400 ring-2 ring-emerald-200" : "border-zinc-200"}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-semibold flex items-center gap-2">
                          {o.carrier.name}
                          {isSelected && <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-bold">Seleccionado</span>}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <StarRating value={o.carrier.rating} size={14} />
                          <span className="text-xs text-zinc-500">{o.carrier.rating || "—"} · {o.carrier.completedServices} servicios</span>
                          {o.driver?.verified && <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full flex items-center gap-1"><CheckCircle size={11} weight="fill" />Conductor verificado</span>}
                        </div>
                        {o.vehicle && (
                          <div className="text-xs text-zinc-500 mt-1 flex items-center gap-1"><Truck size={12} />{o.vehicle.type}{o.vehicle.licensePlate ? ` · ${o.vehicle.licensePlate}` : ""}{o.vehicle.capacity ? ` · ${o.vehicle.capacity}` : ""}</div>
                        )}
                        {o.driver?.name && (
                          <div className="text-xs text-zinc-500 mt-0.5">Conductor: {o.driver.name}{o.driver.licenseClass ? ` · Lic. ${o.driver.licenseClass}` : ""}</div>
                        )}
                      </div>
                      <div className="text-right">
                        {o.type === "ACCEPT" ? (
                          <div className="text-emerald-600 font-semibold text-sm">Acepta tus {formatCLP(o.originalClientPrice)}</div>
                        ) : (
                          <div className="text-[#FF5A36] font-semibold text-sm">Propone</div>
                        )}
                        <div className="font-display text-2xl font-black">{formatCLP(o.offeredPrice)}</div>
                      </div>
                    </div>
                    {o.message && <p className="text-sm text-zinc-600 mt-2 bg-zinc-50 rounded-lg px-3 py-2">"{o.message}"</p>}
                    {!disabled && (
                      <button onClick={() => select(o.id)} disabled={selecting} data-testid={`select-offer-${o.id}`}
                        className="mt-4 w-full py-3 rounded-xl bg-[#FF5A36] text-white font-bold flex items-center justify-center gap-2 hover:bg-[#E04B2A] transition-colors active:scale-95 disabled:opacity-60">
                        {selecting === o.id ? <CircleNotch size={18} className="animate-spin" /> : <><CheckCircle size={18} weight="bold" /> Seleccionar transportista</>}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            </>
          )}
        </section>
      )}
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2.5">
      <Icon size={18} className="text-zinc-400 mt-0.5" />
      <div>
        <div className="text-xs uppercase tracking-widest text-zinc-400 font-bold">{label}</div>
        <div className="font-medium text-sm">{value}</div>
      </div>
    </div>
  );
}
